import "./theme.css";
import { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate, useParams, useSearchParams } from "react-router-dom";
import { QueryClient, QueryClientProvider, useQuery } from "@tanstack/react-query";
import { AuthProvider, useAuth } from "./auth/AuthContext";
import { ProtectedRoute } from "./auth/ProtectedRoute";
import { Layout } from "./components/Layout";
import { EnvBanner } from "./components/EnvBanner";
import { docsEnabled } from "./env";
import { LoginPage } from "./pages/LoginPage";
import { DashboardPage } from "./pages/DashboardPage";
import { TransactionsPage } from "./pages/TransactionsPage";
import { ReportsPage } from "./pages/ReportsPage";
import { RejectionsPage } from "./pages/RejectionsPage";
import { RejectionsSummaryPage } from "./pages/RejectionsSummaryPage";
import { TeamPage } from "./pages/TeamPage";
import { SettingsPage } from "./pages/SettingsPage";
import { OnboardPage } from "./pages/OnboardPage";
import { CustomersPage } from "./pages/CustomersPage";
import { StaffPage } from "./pages/StaffPage";
import { AuditPage } from "./pages/AuditPage";
import { listCustomers } from "./api/portal";
import { DocsErrorBoundary } from "./docs/DocsErrorBoundary";

// Public docs live in their own lazy chunk so the Markdown stack (and Scalar,
// lazily inside it) stays out of the bundle every login downloads.
const DocsRoutes = lazy(() => import("./docs/DocsRoutes"));

const queryClient = new QueryClient();

/**
 * Bridges the :clientId route param into a page prop. Pages take clientId as
 * a prop rather than calling useParams() themselves, so they stay renderable
 * (and testable) outside a Router.
 */
function Scoped({ render }: { render: (clientId: string) => React.ReactNode }) {
  const { clientId } = useParams<{ clientId: string }>();
  if (!clientId) return <Navigate to="/customers" replace />;
  return <>{render(clientId)}</>;
}

/** The list filters a summary-page link may carry. Anything else in the query
 *  string is ignored rather than forwarded to the API as a filter. */
const REJECTION_LINK_FILTERS = ["f_source", "f_kind", "f_reason"] as const;

/** Reads ?from=&to= and the filters above out of the URL, so links into the
 *  Rejections pages land narrowed; the pages themselves stay Router-free. */
function useRejectionLink() {
  const [params] = useSearchParams();
  const from = params.get("from");
  const to = params.get("to");
  const initialRange = from && to ? { from, to } : undefined;
  const initialFilters: Record<string, string> = {};
  for (const k of REJECTION_LINK_FILTERS) {
    const v = params.get(k);
    if (v) initialFilters[k] = v;
  }
  return { initialRange, initialFilters };
}

export function RejectionsSummaryRoute({ clientId, internal }: { clientId?: string; internal?: boolean }) {
  const { initialRange } = useRejectionLink();
  return <RejectionsSummaryPage clientId={clientId} internal={internal} initialRange={initialRange} />;
}

export function RejectionsListRoute({ clientId }: { clientId?: string }) {
  const { initialRange, initialFilters } = useRejectionLink();
  return <RejectionsPage clientId={clientId} initialRange={initialRange} initialFilters={initialFilters} />;
}

function InternalShell({ children }: { children: React.ReactNode }) {
  const { user, logout } = useAuth();
  const { clientId } = useParams<{ clientId: string }>();
  // Already fetched by the switcher; this only names the client in the top bar.
  const { data } = useQuery({ queryKey: ["customers"], queryFn: listCustomers });
  if (!user) return null;
  const scoped = clientId
    ? data?.customers.find((c) => c.client_id === clientId)?.client_name ?? clientId
    : null;
  return (
    <Layout
      role={user.role}
      clientName={user.client_name}
      clientId={clientId}
      scopedClientName={scoped}
      onLogout={logout}
    >
      {children}
    </Layout>
  );
}

function CustomerShell({ children }: { children: React.ReactNode }) {
  const { user, logout } = useAuth();
  if (!user) return null;
  return (
    <Layout role={user.role} clientName={user.client_name} onLogout={logout}>
      {children}
    </Layout>
  );
}

function CustomerRoutes({ role }: { role: "customer_admin" | "customer_user" }) {
  return (
    <CustomerShell>
      <Routes>
        <Route path="/" element={<DashboardPage />} />
        <Route path="/transactions" element={<TransactionsPage />} />
        <Route path="/reports" element={<ReportsPage />} />
        <Route path="/rejections" element={<RejectionsSummaryRoute />} />
        <Route path="/rejections/list" element={<RejectionsListRoute />} />
        {role === "customer_admin" && <Route path="/team" element={<TeamPage />} />}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </CustomerShell>
  );
}

/**
 * The selected customer lives in the URL (/customers/:clientId/...), never in
 * component state -- so an internal view is linkable, back-button-correct,
 * and correct with two tabs open on two customers. See Phase 2 spec §1/§4.
 */
function InternalRoutes({ userId }: { userId?: string }) {
  return (
    <Routes>
      <Route
        path="/customers/:clientId/*"
        element={
          <InternalShell>
            <Routes>
              <Route index element={<Scoped render={(id) => <DashboardPage clientId={id} />} />} />
              <Route path="transactions" element={<Scoped render={(id) => <TransactionsPage clientId={id} />} />} />
              <Route path="reports" element={<Scoped render={(id) => <ReportsPage clientId={id} internal />} />} />
              <Route path="rejections" element={<Scoped render={(id) => <RejectionsSummaryRoute clientId={id} internal />} />} />
              <Route path="rejections/list" element={<Scoped render={(id) => <RejectionsListRoute clientId={id} />} />} />
              <Route path="team" element={<Scoped render={(id) => <TeamPage clientId={id} />} />} />
              <Route path="settings" element={<Scoped render={(id) => <SettingsPage clientId={id} />} />} />
              {/*
                Absolute, not <Navigate to="."/>. Under v7_relativeSplatPath a
                relative "." resolves to the current URL, which turns this
                catch-all into a no-op and strands an unknown sub-path on a
                dead URL inside the scoped shell.
              */}
              <Route
                path="*"
                element={<Scoped render={(id) => <Navigate to={`/customers/${id}`} replace />} />}
              />
            </Routes>
          </InternalShell>
        }
      />
      <Route
        path="/*"
        element={
          <InternalShell>
            <Routes>
              <Route path="/" element={<DashboardPage aggregate />} />
              <Route path="/customers" element={<CustomersPage />} />
              <Route path="/staff" element={<StaffPage currentUserId={userId} />} />
              <Route path="/audit" element={<AuditPage />} />
              <Route path="/onboard" element={<OnboardPage />} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </InternalShell>
        }
      />
    </Routes>
  );
}

/** Exported for routing tests, which mount it under a MemoryRouter. */
export function AppRoutes() {
  const { user } = useAuth();

  return (
    <>
      {/* Above <Routes>, not inside Layout: the login screen renders outside
          Layout entirely, and login is the moment an operator decides which
          environment they are about to work in. */}
      <EnvBanner />
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        {/* Public: outside ProtectedRoute, so docs work with or without a session.
            Not registered in prod, where /docs falls through to the app. */}
        {docsEnabled() && (
          <Route
            path="/docs/*"
            element={
              <DocsErrorBoundary>
                <Suspense fallback={null}>
                  <DocsRoutes />
                </Suspense>
              </DocsErrorBoundary>
            }
          />
        )}
        <Route
          path="/*"
          element={
            <ProtectedRoute>
              {user?.role === "internal" ? (
                <InternalRoutes userId={user.user_id} />
              ) : user ? (
                <CustomerRoutes role={user.role as "customer_admin" | "customer_user"} />
              ) : null}
            </ProtectedRoute>
          }
        />
      </Routes>
    </>
  );
}

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <BrowserRouter>
          <AppRoutes />
        </BrowserRouter>
      </AuthProvider>
    </QueryClientProvider>
  );
}
